<template>
  <div class="cover-block wrap">
    <div class="cover-block__breadcrumbs">
      <SkeletonBlock
        :is-active="isSkeletonActive"
        :width="128"
        :height="20"
      >
        <BreadCrumbs
          v-if="!isSkeletonActive"
          :breadcrumbs
          :is-full-breadcrumbs="fullBreadcrumbsIsShown"
        />
      </SkeletonBlock>
    </div>

    <Text
      as="h1"
      :text="title"
      :skeleton="isSkeletonActive"
      :skeleton-breakpoints="{ default: '680px', tablet: '360px', mobile: '320px' }"
      class="cover-block__title"
      data-test="listing-name"
    />

    <Text
      as="span"
      :text="subtitle"
      :skeleton="isSkeletonActive"
      :skeleton-breakpoints="'308px'"
      class="cover-block__subtitle region-subtitle"
    />
  </div>
</template>

<script setup lang="ts">
  import { BreadCrumbs, generateTourBreadcrumbs, type GenerateBreadcrumbsParams } from '@/entities/breadCrumbs';
  import { computed, watch, ref, nextTick } from 'vue';
  import { useTourSeoInfo } from '@/entities/region/api/useTourSeoInfo.ts';
  import type { CountryTag, RegionInfo, RegionTag } from '@/shared/api/experience';
  import { useRoute } from 'vue-router';
  import { Text } from '@/shared/ui/text';
  import { useLoaderStore } from '@/shared/store/useLoaderStore';
  import { SkeletonBlock } from '@experience/core-skeleton-lib';
  import { createRubricTitlesMap } from '@/entities/rubrics/utils/create-rubric-titles-map.ts';
  import { type FromCityPayload, LocationType } from '@/entities/location';
  import { useRegionTagSeoInfo } from '@/entities/region/api/useRegionTagSeoInfo.ts';
  import { useHead } from '@unhead/vue';
  import { useTourFiltersStore } from '@/entities/filter/store/tour';
  import { useSeoStore } from '@/shared/store/useSeoStore.ts';
  import { useListingSkeleton } from '@/shared/composables/useDelayedListingSkeleton.ts';
  import { useTourInRegionFromCitySeoInfo } from '@/entities/region/api/useTourInRegionFromCitySeoInfo.ts';
  import { useTagCanonicalLink } from '@/entities/tag/composables/useCanonicalLinks.ts';

  interface Props {
    currentTag: string;
    slug: string;
    page: number;
    fromCitySeoPayload?: Nullable<FromCityPayload>;
    geoObject: RegionInfo;
    rubric?: string | null;
    tags: RegionTag[] | CountryTag[];
    location: LocationType;
  }

  const props = defineProps<Props>();

  const loaderStore = useLoaderStore();
  const filterStore = useTourFiltersStore();
  const seoStore = useSeoStore();

  const route = useRoute();

  // /experience/123
  const isExperienceDetailsPage = computed(() => /^\/experience\/\d+$/.test(route.path));

  const { isSkeletonActive } = useListingSkeleton();

  const { tourInRegionFromCitySeoData } = useTourInRegionFromCitySeoInfo(
    computed(() => props.fromCitySeoPayload?.regionSlug),
    computed(() => props.fromCitySeoPayload?.citySlug),
    computed(() => !!props.fromCitySeoPayload),
  );

  const {
    tourSeoData,
    isSuccess: isTourSeoLoaded,
    isFetching: isTourSeoFetching,
  } = useTourSeoInfo(
    computed(() => 'region_tours'),
    computed(() => props.slug),
    computed(() => props.currentTag === 'all'),
  );

  const {
    regionTagSeoData,
    isFetching: isRegionTagSeoFetching,
    isSuccess: isRegionTagSeoLoaded,
  } = useRegionTagSeoInfo(
    computed(() => `${props.slug}__${props.currentTag}`),
    computed(() => props.currentTag !== 'all'),
  );

  const seoLoadStartTime = ref<number | null>(null);

  // Watch for location changes to reset timer
  watch(
    () => props.slug,
    () => {
      seoLoadStartTime.value = null;
    },
  );

  // Watch for tag changes to reset seo flag with timer
  watch(
    () => props.currentTag,
    (newTag, oldTag) => {
      if (oldTag !== undefined && newTag !== oldTag) {
        loaderStore.ready.seo = false;
        seoLoadStartTime.value = Date.now();

        // If data already loaded (from cache), set ready immediately
        nextTick(() => {
          if (seoIsLoaded.value && !seoIsFetching.value && !loaderStore.ready.seo) {
            handleSeoLoaded();
          }
        });
      }
    },
  );

  const seoIsLoaded = computed(() => {
    if (props.currentTag === 'all' || props.currentTag === '') {
      return Boolean(isTourSeoLoaded.value);
    }

    return Boolean(isRegionTagSeoLoaded.value);
  });

  const seoIsFetching = computed(() => {
    if (props.currentTag === 'all' || props.currentTag === '') {
      return Boolean(isTourSeoFetching.value);
    }

    return Boolean(isRegionTagSeoFetching.value);
  });

  const setSeoReady = () => {
    loaderStore.ready.seo = true;
    seoLoadStartTime.value = null;
  };

  const handleSeoLoaded = () => {
    if (seoLoadStartTime.value !== null) {
      const elapsed = Date.now() - seoLoadStartTime.value;
      const minDisplayTime = 800;
      const remainingTime = Math.max(0, minDisplayTime - elapsed);

      // If data loaded instantly (from cache), don't delay
      if (elapsed < 50) {
        setSeoReady();
      } else if (remainingTime > 0) {
        setTimeout(setSeoReady, remainingTime);
      } else {
        setSeoReady();
      }
    } else {
      setSeoReady();
    }
  };

  watch(
    () => [seoIsLoaded.value, seoIsFetching.value],
    ([isLoaded, isFetching]) => {
      // Don't reset ready.seo during background refetch (when data is already loaded)
      if (isFetching && !isLoaded) {
        if (loaderStore.ready.seo) {
          loaderStore.ready.seo = false;
        }

        return;
      }

      if (isLoaded && !isFetching && !loaderStore.ready.seo) {
        handleSeoLoaded();
      }
    },
    { immediate: true },
  );
  const isAllRubricLabel = computed(() =>
    props.currentTag === 'all' || props.currentTag === '' ? tourSeoData.value?.seo : regionTagSeoData.value?.seo,
  );

  const title = computed(() => {
    const defaultValue = isSkeletonActive.value ? '' : 'Tripster';

    const locationTitle = isAllRubricLabel.value?.h1 || defaultValue;

    return props.fromCitySeoPayload ? tourInRegionFromCitySeoData.value?.seo?.h1 || defaultValue : locationTitle;
  });
  const subtitle = computed(() => tourSeoData?.value?.seo?.h2 || '');
  const headTitle = computed(() => {
    const locationTitle = isAllRubricLabel.value?.title || 'Tripster';

    return props.fromCitySeoPayload ? tourInRegionFromCitySeoData.value?.seo?.title || 'Tripster' : locationTitle;
  });

  const noindex = computed(() => regionTagSeoData?.value?.seo?.noindex || false);

  const seoTitleWithPageNumber = (formatTitle: string, pageNumber = 1) => {
    const amountOfExpOnPage = 54;

    const lastPageNumber = filterStore.searchTotalCount
      ? Math.floor(filterStore.searchTotalCount / amountOfExpOnPage)
      : 0;

    if (formatTitle && lastPageNumber) {
      return Number(pageNumber) > 1 ? `${formatTitle} | Страница ${pageNumber} из ${lastPageNumber}` : formatTitle;
    }

    return formatTitle || '';
  };

  const seoTitle = computed(() => seoTitleWithPageNumber(toValue(headTitle.value), Number(route.query?.page || 1)));

  const description = computed(() => {
    if (route.query?.page && Number(route.query.page) > 1) {
      return '';
    }

    if (!!props.fromCitySeoPayload) {
      return tourInRegionFromCitySeoData.value?.seo?.description || '';
    }

    return isAllRubricLabel.value?.description || '';
  });

  watch(
    () => headTitle.value,
    (value) => (document.title = value),
    { immediate: true },
  );

  const breadcrumbs = computed(() => {
    const params: GenerateBreadcrumbsParams = {
      currentTag: props.currentTag,
      geoObject: props.geoObject,
      rubric: props.rubric ?? null,
      rubricTitlesMap: createRubricTitlesMap(props.tags),
      isExperienceDetailsPage: isExperienceDetailsPage.value,
      locationType: LocationType.REGION,
    };

    params.country = route.params.country as string;
    params.regionSlug = route.params.regionSlug as string;
    params.regionName = (props.geoObject as RegionInfo).name;
    params.countryName = (props.geoObject as RegionInfo).countries?.[0]?.name_ru;

    return generateTourBreadcrumbs(params);
  });

  const fullBreadcrumbsIsShown = computed(() => props.currentTag !== 'all');
  const canonicalLink = useTagCanonicalLink(computed(() => props.currentTag));

  useHead({
    title: () => seoTitle.value,
    link: [
      {
        rel: 'canonical',
        href: () => canonicalLink.value,
      },
    ],
    meta: () => {
      const metaArr = [
        {
          name: 'description',
          content: () => description.value,
        },
        {
          property: 'og:title',
          content: () => seoTitle.value,
        },
        {
          property: 'og:description',
          content: () => description.value,
        },
        {
          content: () => seoStore.resizeImage,
          property: 'og:image',
        },
        {
          property: 'og:url',
          content: () => canonicalLink.value,
        },
        {
          name: 'twitter:card',
          content: 'summary_large_image',
        },
        {
          name: 'twitter:image',
          content: () => seoStore.resizeImage,
        },
      ];

      if (noindex.value) {
        metaArr.push({
          name: 'robots',
          content: 'noindex, nofollow',
        });
      }

      return metaArr;
    },
  });
</script>

<style lang="scss" scoped>
  @use '@experience/design-tokens/typography' as typography;
  @use '@experience/shared/dist/assets/adaptivity' as adaptivity;

  .cover-block__title {
    margin-top: 8px;
    margin-bottom: 4px;
    text-wrap: balance;

    @include typography.base-1000-950-extra;

    @include adaptivity.tablet {
      @include typography.base-700-500-extra;
    }
  }

  .cover-block__subtitle {
    display: block;
    overflow: hidden;
    margin-top: 8px;
    margin-bottom: 4px;
    text-overflow: ellipsis;
    hyphens: auto;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;

    @include typography.base-300-300-medium;
  }
</style>
